import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE } from "../apiBase";

const API = API_BASE;

export default function SetPasswordPage() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [status, setStatus] = useState(token ? "loading" : "create");
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!token) return;
        axios.get(`${API}/api/auth/verify-token/${token}`)
            .then(({ data }) => {
                if (data.valid) { setStatus("ready"); setEmail(data.email); }
                else setStatus("invalid");
            })
            .catch(() => setStatus("invalid"));
    }, [token]);

    // Token-based set password
    const handleTokenSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
        if (password !== confirm) { setError("Passwords don't match"); return; }
        setBusy(true);
        try {
            await axios.post(`${API}/api/auth/set-password`, { token, password });
            setStatus("success");
        } catch (err) {
            setError(err.response?.data?.detail || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    // Direct create password (no token)
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!email.trim()) { setError("Please enter your email"); return; }
        if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
        if (password !== confirm) { setError("Passwords don't match"); return; }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/api/auth/create-password`, { email: email.trim(), password }, { withCredentials: true });
            if (data.access_token) {
                localStorage.setItem("token", data.access_token);
            }
            setStatus("created");
        } catch (err) {
            setError(err.response?.data?.detail || "Something went wrong");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{
            minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
            background: "var(--pord-bg, #1a1e14)", fontFamily: "'DM Sans', sans-serif",
        }}>
            <div style={{
                width: 380, padding: "40px 36px", borderRadius: 12,
                background: "rgba(184,212,208,0.03)", border: "1px solid rgba(184,212,208,0.08)",
            }}>
                <h1 style={{ fontSize: 36, fontWeight: 300, color: "#DAF2D0", marginBottom: 6, letterSpacing: 2 }}>Pord.</h1>

                {status === "loading" && (
                    <p style={{ fontSize: 13, color: "rgba(255,255,255,0.4)" }}>Verifying link...</p>
                )}

                {status === "invalid" && (
                    <div>
                        <p style={{ fontSize: 14, color: "#e57373", marginBottom: 16 }}>This link is invalid or has expired.</p>
                        <button onClick={() => navigate("/")} style={linkStyle}>Back to sign in</button>
                    </div>
                )}

                {/* Token-based flow */}
                {status === "ready" && (
                    <>
                        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginBottom: 24 }}>
                            Set your password for <strong style={{ color: "#DAF2D0" }}>{email}</strong>
                        </p>
                        <form onSubmit={handleTokenSubmit}>
                            <input type="password" placeholder="New password" value={password}
                                onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
                            <input type="password" placeholder="Confirm password" value={confirm}
                                onChange={(e) => setConfirm(e.target.value)} required style={inputStyle} />
                            {error && <div style={{ color: "#e57373", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                            <button type="submit" disabled={busy} className="cta-pill cta-pill-filled" style={btnStyle(busy)}>
                                {busy ? "..." : "Set Password"}
                            </button>
                        </form>
                    </>
                )}

                {/* Direct create password (no token) */}
                {status === "create" && (
                    <>
                        <p style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginBottom: 24 }}>
                            Create your password
                        </p>
                        <form onSubmit={handleCreateSubmit}>
                            <input type="email" placeholder="Email" value={email}
                                onChange={(e) => setEmail(e.target.value)} required style={inputStyle} />
                            <input type="password" placeholder="Password" value={password}
                                onChange={(e) => setPassword(e.target.value)} required style={inputStyle} />
                            <input type="password" placeholder="Confirm password" value={confirm}
                                onChange={(e) => setConfirm(e.target.value)} required style={inputStyle} />
                            {error && <div style={{ color: "#e57373", fontSize: 12, marginBottom: 12 }}>{error}</div>}
                            <button type="submit" disabled={busy} className="cta-pill cta-pill-filled" style={btnStyle(busy)}>
                                {busy ? "..." : "Create Password"}
                            </button>
                        </form>
                        <div style={{ textAlign: "center", marginTop: 12 }}>
                            <button onClick={() => navigate("/")} style={linkStyle}>Back to sign in</button>
                        </div>
                    </>
                )}

                {status === "success" && (
                    <div>
                        <p style={{ fontSize: 14, color: "#DAF2D0", marginBottom: 16 }}>Password set successfully!</p>
                        <button onClick={() => navigate("/")} className="cta-pill cta-pill-filled" style={btnStyle(false)}>
                            Sign In
                        </button>
                    </div>
                )}

                {status === "created" && (
                    <div>
                        <p style={{ fontSize: 14, color: "#DAF2D0", marginBottom: 16 }}>Password created! You're all set.</p>
                        <button onClick={() => { window.location.href = "/positioning"; }} className="cta-pill cta-pill-filled" style={btnStyle(false)}>
                            Go to Dashboard
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

const inputStyle = {
    width: "100%", padding: "11px 14px", marginBottom: 14, borderRadius: 8,
    border: "1px solid rgba(184,212,208,0.12)", background: "rgba(30,34,24,0.8)",
    color: "#DAF2D0", fontSize: 13, outline: "none", fontFamily: "'DM Sans', sans-serif",
    boxSizing: "border-box",
};

const btnStyle = (busy) => ({
    width: "100%", padding: "12px 0", borderRadius: 8, fontSize: 14, fontWeight: 500,
    justifyContent: "center", cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1,
    fontFamily: "'DM Sans', sans-serif", marginBottom: 16,
});

const linkStyle = {
    background: "none", border: "none", color: "#DAF2D0", cursor: "pointer",
    fontSize: 12, textDecoration: "underline", padding: 0, fontFamily: "'DM Sans', sans-serif",
};
